import React, { useEffect } from 'react';
import './PrivacyPolicy.css';
import NavBar from '../../components/NavBar/NavBar';
import Footer from '../../components/Footer/Footer';
import { useLanguage } from "../../contexts/LanguageContext";
import LanguageSwitcher from '../../components/LanguageSwitcher/LanguageSwitcher';
import FloatingWebsiteNavigation from '../../components/FloatingWebsiteNavigation/FloatingWebsiteNavigation';

function PrivacyPolicy() {
  const { translations: tRaw, language } = useLanguage();
  const t = { terms: {}, ...tRaw, terms: { ...tRaw?.terms } }; // Safe fallback

  const isRTL = language === 'ar' || language === 'fa' || language === 'ur';

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, { threshold: 0.1 });

    document.querySelectorAll('.scroll-animate').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <div className="nav-desktop-only">
        <NavBar />
      </div>
      <div className="privacy-container" dir={isRTL ? 'rtl' : 'ltr'}>
        <header className="privacy-header scroll-animate">
          <h1>{t.privacy?.title || "Privacy Policy"}</h1>
          <p className="last-updated">
            {t.privacy?.lastUpdated || "Last updated"}: {new Date().toLocaleDateString()}
          </p>
        </header>

        <div className="privacy-content">
          {/* Section 1 */}
          <section className="policy-section scroll-animate">
            <h2>{t.privacy?.section1?.title || "1. Data Protection Policy"}</h2>

            <article className="policy-article">
              <h3>{t.privacy?.section1?.article1?.title || "1.1. Data Collection and Storage"}</h3>
              <ul>
                <li>{t.privacy?.section1?.article1?.item1 || "Personal data of users is collected and stored in compliance with local and international privacy protection laws."}</li>
                <li>{t.privacy?.section1?.article1?.item2 || "Data is used solely for specific purposes, such as improving application services, providing technical support, and ensuring quality performance."}</li>
                <li>{t.privacy?.section1?.article1?.item3 || "The sale or sharing of personal data with third parties is strictly prohibited without the user’s explicit consent."}</li>
              </ul>
            </article>

            <article className="policy-article">
              <h3>{t.privacy?.section1?.article2?.title || "1.2. User Rights"}</h3>
              <ul>
                <li>{t.privacy?.section1?.article2?.item1 || "The right to access their personal data."}</li>
                <li>{t.privacy?.section1?.article2?.item2 || "The right to request modifications or deletion of data when necessary."}</li>
                <li>{t.privacy?.section1?.article2?.item3 || "The right to file complaints regarding any violation of the privacy policy."}</li>
                <li>{t.privacy?.section1?.article2?.item4 || "The right to receive periodic notifications about any changes to privacy policies."}</li>
              </ul>
            </article>

            <article className="policy-article">
              <h3>{t.privacy?.section1?.article3?.title || "1.3. Cybersecurity"}</h3>
              <ul>
                <li>{t.privacy?.section1?.article3?.item1 || "Adoption of advanced encryption technologies to ensure data protection against cybersecurity threats."}</li>
                <li>{t.privacy?.section1?.article3?.item2 || "Continuous monitoring of suspicious activities and immediate reporting to relevant authorities."}</li>
                <li>{t.privacy?.section1?.article3?.item3 || "Training the team on cybersecurity procedures and best technical practices."}</li>
              </ul>
            </article>
          </section>

          {/* Section 2 */}
          <section className="policy-section scroll-animate">
            <h2>{t.privacy?.section2?.title || "2. Information We Collect"}</h2>

            <article className="policy-article">
              <h3>{t.privacy?.section2?.article1?.title || "2.1. Information Provided Directly by Users"}</h3>
              <ul>
                <li>{t.privacy?.section2?.article1?.item1 || "Full name, email address, phone number, and payment details."}</li>
                <li>{t.privacy?.section2?.article1?.item2 || "Data submitted via forms such as registration or technical support requests."}</li>
              </ul>
            </article>

            <article className="policy-article">
              <h3>{t.privacy?.section2?.article2?.title || "2.2. Automatically Collected Data"}</h3>
              <ul>
                <li>{t.privacy?.section2?.article2?.item1 || "Device data, including device type, operating system, and application version."}</li>
                <li>{t.privacy?.section2?.article2?.item2 || "User’s IP address."}</li>
                <li>{t.privacy?.section2?.article2?.item3 || "Usage logs such as login times, session duration, and in‑app interactions."}</li>
              </ul>
            </article>

            <article className="policy-article">
              <h3>{t.privacy?.section2?.article3?.title || "2.3. Data from Third Parties"}</h3>
              <p>{t.privacy?.section2?.article3?.text || "We may receive information from partners or external service providers to enhance user experience."}</p>
            </article>
          </section>

          {/* Section 3 */}
          <section className="policy-section scroll-animate">
            <h2>{t.privacy?.section3?.title || "3. How We Use Information"}</h2>
            <article className="policy-article">
              <h3>{t.privacy?.section3?.article1?.title || "3.1. Use of Collected Data"}</h3>
              <ul>
                <li>{t.privacy?.section3?.article1?.item1 || "To improve service quality and enhance user experience."}</li>
                <li>{t.privacy?.section3?.article1?.item2 || "To process payments and securely verify transactions."}</li>
                <li>{t.privacy?.section3?.article1?.item3 || "To provide technical support and respond to user inquiries."}</li>
                <li>{t.privacy?.section3?.article1?.item4 || "To analyze usage patterns and optimize application performance."}</li>
                <li>{t.privacy?.section3?.article1?.item5 || "To send updates and important notifications regarding the application."}</li>
              </ul>
            </article>

            <article className="policy-article">
              <h3>{t.privacy?.section3?.article2?.title || "3.2. Data Sharing Policy"}</h3>
              <p>{t.privacy?.section3?.article2?.text || "Personal data will not be shared with third parties without explicit user consent, except in the following cases:"}</p>
              <ul>
                <li>{t.privacy?.section3?.article2?.item1 || "When required by law or regulatory authorities."}</li>
                <li>{t.privacy?.section3?.article2?.item2 || "With trusted service providers assisting in application operations under strict confidentiality agreements."}</li>
              </ul>
            </article>

            <article className="policy-article">
              <h3>{t.privacy?.section3?.article3?.title || "3.3. Data Retention and Deletion"}</h3>
              <ul>
                <li>{t.privacy?.section3?.article3?.item1 || "User data is retained only as long as necessary for the purposes outlined in this policy."}</li>
                <li>{t.privacy?.section3?.article3?.item2 || "Users may request data deletion at any time, subject to legal and operational constraints."}</li>
              </ul>
            </article>
          </section>

          {/* Section 4 */}
          <section className="policy-section scroll-animate">
            <h2>{t.privacy?.section4?.title || "4. Data Disclosure"}</h2>
            <p>{t.privacy?.section4?.text || "We do not sell or rent your personal data to any third party. However, data may be shared in the following cases:"}</p>

            <article className="policy-article">
              <h3>{t.privacy?.section4?.article1?.title || "4.1. Service Providers"}</h3>
              <p>{t.privacy?.section4?.article1?.text || "To facilitate services such as payment processing or server hosting."}</p>
            </article>

            <article className="policy-article">
              <h3>{t.privacy?.section4?.article2?.title || "4.2. Government and Legal Authorities"}</h3>
              <p>{t.privacy?.section4?.article2?.text || "In response to legal requests or compliance with legal obligations."}</p>
            </article>

            <article className="policy-article">
              <h3>{t.privacy?.section4?.article3?.title || "4.3. Business Partners"}</h3>
              <p>{t.privacy?.section4?.article3?.text || "In cases of mergers or acquisitions, with prior notification to users."}</p>
            </article>
          </section>

          {/* Section 5 */}
          <section className="policy-section scroll-animate">
            <h2>{t.privacy?.section5?.title || "5. User Rights"}</h2>
            <ul>
              <li>{t.privacy?.section5?.item1 || "Access and correct their personal data."}</li>
              <li>{t.privacy?.section5?.item2 || "Request data deletion unless retention is required for legal compliance."}</li>
              <li>{t.privacy?.section5?.item3 || "Object to data processing for marketing purposes."}</li>
              <li>{t.privacy?.section5?.item4 || "Request data transfer to another system."}</li>
            </ul>
          </section>

          {/* Section 6 */}
          <section className="policy-section scroll-animate">
            <h2>{t.privacy?.section6?.title || "6. Data Storage and Security"}</h2>
            <ul>
              <li>{t.privacy?.section6?.item1 || "Data is stored on secure servers using advanced encryption technologies."}</li>
              <li>{t.privacy?.section6?.item2 || "Strict protection measures are implemented to prevent unauthorized access."}</li>
              <li>{t.privacy?.section6?.item3 || "Data is retained only for the duration necessary to fulfill its intended purpose."}</li>
            </ul>
          </section>

          {/* Section 7 */}
          <section className="policy-section scroll-animate">
            <h2>{t.privacy?.section7?.title || "7. Cookies and Similar Technologies"}</h2>
            <ul>
              <li>{t.privacy?.section7?.item1 || "We use cookies to enhance browsing experience."}</li>
              <li>{t.privacy?.section7?.item2 || "Users can modify browser settings to control cookie preferences."}</li>
            </ul>
          </section>

          {/* Section 8 */}
          <section className="policy-section scroll-animate">
            <h2>{t.privacy?.section8?.title || "8. Payment Policy and Financial Transactions"}</h2>

            <article className="policy-article">
              <h3>{t.privacy?.section8?.article1?.title || "8.1. Supported Payment Methods"}</h3>
              <ul>
                <li>{t.privacy?.section8?.article1?.item1 || "Direct in-app payments using bank cards or digital wallets."}</li>
                <li>{t.privacy?.section8?.article1?.item2 || "Use of virtual and physical payment cards issued in partnership with reputable financial institutions."}</li>
                <li>{t.privacy?.section8?.article1?.item3 || "Integration with various payment devices such as POS terminals and mobile payment systems."}</li>
              </ul>
            </article>

            <article className="policy-article">
              <h3>{t.privacy?.section8?.article2?.title || "8.2. Transaction Conditions"}</h3>
              <ul>
                <li>{t.privacy?.section8?.article2?.item1 || "All financial transactions require identity verification for security purposes."}</li>
                <li>{t.privacy?.section8?.article2?.item2 || "A minimal fee may apply to certain transactions based on transfer type and user location."}</li>
                <li>{t.privacy?.section8?.article2?.item3 || "Users can track all transactions through a dedicated transaction log for transparency."}</li>
              </ul>
            </article>

            <article className="policy-article">
              <h3>{t.privacy?.section8?.article3?.title || "8.3. Guarantees"}</h3>
              <ul>
                <li>{t.privacy?.section8?.article3?.item1 || "A complete transaction history is provided to protect user rights."}</li>
                <li>{t.privacy?.section8?.article3?.item2 || "Strict fraud and anti-money laundering policies using AI-powered monitoring."}</li>
                <li>{t.privacy?.section8?.article3?.item3 || "Refund guarantees in case of technical errors or unauthorized transactions."}</li>
              </ul>
            </article>
          </section>

          {/* Section 9 */}
          <section className="policy-section scroll-animate">
            <h2>{t.privacy?.section9?.title || "9. Prevention of Pyramid Schemes and Money Laundering"}</h2>

            <article className="policy-article">
              <h3>{t.privacy?.section9?.article1?.title || "9.1. Preventive Measures"}</h3>
              <ul>
                <li>{t.privacy?.section9?.article1?.item1 || "Absolute prohibition of any pyramid or network marketing activities."}</li>
                <li>{t.privacy?.section9?.article1?.item2 || "Implementation of AI-driven monitoring to detect illegal activities."}</li>
                <li>{t.privacy?.section9?.article1?.item3 || "Periodic account and activity reviews to ensure compliance."}</li>
              </ul>
            </article>

            <article className="policy-article">
              <h3>{t.privacy?.section9?.article2?.title || "9.2. Reporting and Accountability"}</h3>
              <ul>
                <li>{t.privacy?.section9?.article2?.item1 || "Users can report suspicious activities through dedicated communication channels."}</li>
                <li>{t.privacy?.section9?.article2?.item2 || "Collaboration with legal authorities to prosecute violators and enforce appropriate penalties."}</li>
              </ul>
            </article>

            <article className="policy-article">
              <h3>{t.privacy?.section9?.article3?.title || "9.3. Compliance with International Regulations"}</h3>
              <ul>
                <li>{t.privacy?.section9?.article3?.item1 || "Adherence to local and international regulations on anti-money laundering and counter‑terrorism financing."}</li>
                <li>{t.privacy?.section9?.article3?.item2 || "Training teams on best practices to combat illegal activities."}</li>
              </ul>
            </article>
          </section>

          {/* Section 10 */}
          <section className="policy-section scroll-animate">
            <h2>{t.privacy?.section10?.title || "10. Legal Commitments"}</h2>

            <article className="policy-article">
              <h3>{t.privacy?.section10?.article1?.title || "10.1. Compliance with Laws"}</h3>
              <ul>
                <li>{t.privacy?.section10?.article1?.item1 || "The application adheres to all local and international regulations related to e‑commerce, data protection, and consumer rights."}</li>
                <li>{t.privacy?.section10?.article1?.item2 || "Commitment to global anti‑money laundering and counter‑terrorism financing policies."}</li>
              </ul>
            </article>

            <article className="policy-article">
              <h3>{t.privacy?.section10?.article2?.title || "10.2. Dispute Resolution"}</h3>
              <ul>
                <li>{t.privacy?.section10?.article2?.item1 || "Disputes are referred to the competent judicial authorities under local laws."}</li>
                <li>{t.privacy?.section10?.article2?.item2 || "Users may request arbitration under the stated terms and conditions for a quick and amicable settlement."}</li>
              </ul>
            </article>
          </section>

          {/* Section 11 */}
          <section className="policy-section scroll-animate">
            <h2>{t.privacy?.section11?.title || "11. Final Remarks"}</h2>
            <p>{t.privacy?.section11?.text || "This document provides a comprehensive legal framework ensuring the rights of all parties involved in iWinto."}</p>

            <article className="policy-article">
              <h3>{t.privacy?.section11?.article1?.title || "11.1. Communication and Contact Methods"}</h3>
              <ul>
                <li>{t.privacy?.section11?.article1?.item1 || "Email: support@iwinto.com"}</li>
                <li>{t.privacy?.section11?.article1?.item2 || "Phone: +971503780135"}</li>
                <li>{t.privacy?.section11?.article1?.item3 || "Email Support: Users can contact us via our official email for inquiries and feedback."}</li>
                <li>{t.privacy?.section11?.article1?.item4 || "Technical Support: A dedicated 24/7 support section is available to resolve any technical issues."}</li>
                <li>{t.privacy?.section11?.article1?.item5 || "Office Visits: Users may visit our local offices for direct consultations or to address issues."}</li>
                <li>{t.privacy?.section11?.article1?.item6 || "Complaint Procedures: Formal complaint mechanisms exist for users to report grievances."}</li>
              </ul>
            </article>
          </section>
          <section className="policy-section scroll-animate">
            <h2>{t.privacy?.section12?.title || "12. Governing Laws"}</h2>
            <p>{t.privacy?.section12?.text || "This policy is governed by the laws of the United Arab Emirates, in compliance with any applicable international regulations."}</p>
          </section>

          {/* Section 13 */}
          <section className="policy-section scroll-animate">
            <h2>{t.privacy?.section13?.title || "13. Consent and Documentation"}</h2>
            <p>{t.privacy?.section13?.text || "By using the iWinto website and application, you agree to all terms of this Privacy Policy. If you do not agree, please discontinue using our services immediately."}</p>
          </section>

          {/* Section 14 */}
          <section className="policy-section scroll-animate">
            <h2>{t.privacy?.section14?.title || "14. Disclaimer"}</h2>
            <p>{t.privacy?.section14?.text || "We make every effort to ensure the security of user data; however, due to the nature of the internet, absolute security cannot be guaranteed."}</p>
          </section>

          {/* Section 15 */}
          <section className="policy-section scroll-animate">
            <h2>{t.privacy?.section15?.title || "15. Privacy Policy Updates"}</h2>
            <p>{t.privacy?.section15?.text || "We reserve the right to modify this policy at any time. Users will be informed of any significant changes."}</p>
          </section>
        </div>
      </div>
      <Footer />
      <FloatingWebsiteNavigation />
    </>
  );
}

export default PrivacyPolicy;
